import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import CompliancePulsePanel from "../components/CompliancePulsePanel";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Loader2, ShieldCheck, ShieldAlert, RefreshCw, Lock, Unlock, AlertTriangle, Activity, Users, UserCheck, FileLock } from "lucide-react";
import { toast } from "sonner";

const LEVEL_BADGE = {
  critical: "bg-red-500/10 text-red-400 border-red-500/40",
  warning: "bg-amber-500/10 text-amber-400 border-amber-500/40",
  info: "bg-sky-500/10 text-sky-400 border-sky-500/40",
};

const LEVEL_ICON = {
  critical: ShieldAlert,
  warning: AlertTriangle,
  info: Activity,
};

function Stat({ label, value, sub, icon: Icon, testid }) {
  return (
    <div className="bg-[var(--pa-surface)] border border-[var(--pa-border)] rounded-lg p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-[var(--pa-text-muted)] text-xs uppercase tracking-widest">
        {Icon && <Icon size={14} />}
        <span>{label}</span>
      </div>
      <div className="font-serif text-3xl text-[var(--pa-text)] mt-2">{value}</div>
      {sub && <div className="text-xs text-[var(--pa-text-dim)] mt-1">{sub}</div>}
    </div>
  );
}

export default function SecurityAdmin() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [locked, setLocked] = useState([]);
  const [failed, setFailed] = useState([]);
  const [phi, setPhi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unlocking, setUnlocking] = useState(null);

  useEffect(() => {
    if (user && !user.is_admin) nav("/dashboard", { replace: true });
  }, [user, nav]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [m, a, l, f, p] = await Promise.all([
        api.get("/security-admin/metrics"),
        api.get("/security-admin/alerts"),
        api.get("/security-admin/locked-accounts"),
        api.get("/security-admin/recent-failed-logins", { params: { limit: 25 } }),
        api.get("/security-admin/recent-phi-access", { params: { limit: 50 } }),
      ]);
      setMetrics(m.data);
      setAlerts(a.data.items || []);
      setLocked(l.data.items || []);
      setFailed(f.data.items || []);
      setPhi(p.data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load security dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user?.is_admin) loadAll(); }, [user]);

  const unlock = async (uid, email) => {
    setUnlocking(uid);
    try {
      await api.post(`/security-admin/unlock/${uid}`);
      toast.success(`Unlocked ${email}`);
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not unlock");
    } finally {
      setUnlocking(null);
    }
  };

  if (loading || !metrics) {
    return (
      <Layout>
        <div className="flex items-center gap-2 text-sm text-[var(--pa-text-muted)]" data-testid="security-admin-loading">
          <Loader2 size={14} className="animate-spin" /> Loading security dashboard…
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex items-start justify-between">
        <div>
          <div className="kicker">Admin · Security</div>
          <h1 className="font-serif text-3xl md:text-4xl text-[var(--pa-text)] mt-2" data-testid="security-admin-title">
            Security operations
          </h1>
          <p className="mt-2 text-sm text-[var(--pa-text-muted)] max-w-2xl">
            Live signal on authentication health, account lockouts, PHI access, and active threats.
          </p>
        </div>
        <button
          onClick={loadAll}
          data-testid="security-admin-refresh"
          className="text-xs text-[var(--pa-gold)] hover:underline inline-flex items-center gap-1.5"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      <div className="gold-divider my-8" />

      <CompliancePulsePanel />

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="mb-8 space-y-3" data-testid="security-admin-alerts">
          {alerts.map((a, i) => {
            const Icon = LEVEL_ICON[a.level] || Activity;
            return (
              <div
                key={i}
                className={`flex items-start gap-3 p-4 rounded-lg border ${LEVEL_BADGE[a.level] || LEVEL_BADGE.info}`}
                data-testid={`alert-${a.level}`}
              >
                <Icon size={18} className="mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">{a.title}</div>
                  <div className="text-xs opacity-80 mt-0.5">{a.detail}</div>
                </div>
                <span className="text-[10px] uppercase tracking-widest opacity-70 shrink-0">{a.level}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Stat
          label="2FA enrollment"
          value={`${metrics.users.totp_enrollment_pct}%`}
          sub={`${metrics.users.totp_enrolled} / ${metrics.users.total} users`}
          icon={ShieldCheck}
          testid="stat-2fa-enrollment"
        />
        <Stat
          label="Admin 2FA"
          value={`${metrics.users.admin_enrollment_pct}%`}
          sub={`${metrics.users.admins_enrolled} / ${metrics.users.admins_total} admins`}
          icon={UserCheck}
          testid="stat-admin-2fa"
        />
        <Stat
          label="Locked accounts"
          value={metrics.users.locked_now}
          sub="Currently in lockout window"
          icon={Lock}
          testid="stat-locked"
        />
        <Stat
          label="Users total"
          value={metrics.users.total}
          sub="All clinicians + admins"
          icon={Users}
          testid="stat-users"
        />
        <Stat
          label="Login success (24h)"
          value={metrics.last_24h.login_success}
          icon={Activity}
          testid="stat-success-24h"
        />
        <Stat
          label="Failed logins (24h)"
          value={metrics.last_24h.login_failed}
          icon={ShieldAlert}
          testid="stat-failed-24h"
        />
        <Stat
          label="Lockouts (24h)"
          value={metrics.last_24h.lockouts}
          icon={Lock}
          testid="stat-lockouts-24h"
        />
        <Stat
          label="PHI access (24h)"
          value={metrics.last_24h.phi_access}
          sub="Read · write · download"
          icon={FileLock}
          testid="stat-phi-24h"
        />
      </div>

      {/* Locked accounts */}
      <section className="mb-8">
        <h2 className="font-serif text-xl text-[var(--pa-text)] mb-3 flex items-center gap-2">
          <Lock size={16} className="text-[var(--pa-gold)]" /> Locked accounts
        </h2>
        {locked.length === 0 ? (
          <div className="text-xs text-[var(--pa-text-dim)]" data-testid="no-locked">No accounts are currently locked.</div>
        ) : (
          <div className="overflow-x-auto bg-[var(--pa-surface)] border border-[var(--pa-border)] rounded-lg">
            <table className="w-full text-sm" data-testid="locked-table">
              <thead className="text-[var(--pa-text-muted)] text-xs uppercase tracking-wider">
                <tr><th className="p-3 text-left">Email</th><th className="p-3 text-left">Failed</th><th className="p-3 text-left">Locked Until</th><th className="p-3 text-right">Action</th></tr>
              </thead>
              <tbody>
                {locked.map((r) => (
                  <tr key={r.id} className="border-t border-[var(--pa-border)]" data-testid={`locked-row-${r.id}`}>
                    <td className="p-3 text-[var(--pa-text)]">{r.email} {r.is_admin && <span className="text-[10px] text-amber-400 ml-1">ADMIN</span>}</td>
                    <td className="p-3 text-[var(--pa-text-muted)]">{r.failed_login_attempts}</td>
                    <td className="p-3 text-[var(--pa-text-muted)] text-xs">{new Date(r.lockout_until).toLocaleString()}</td>
                    <td className="p-3 text-right">
                      <button
                        onClick={() => unlock(r.id, r.email)}
                        disabled={unlocking === r.id}
                        data-testid={`unlock-${r.id}`}
                        className="inline-flex items-center gap-1 text-xs text-[var(--pa-gold)] hover:underline disabled:opacity-50"
                      >
                        {unlocking === r.id ? <Loader2 size={12} className="animate-spin" /> : <Unlock size={12} />}
                        Unlock
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Recent failed logins */}
      <section className="mb-8">
        <h2 className="font-serif text-xl text-[var(--pa-text)] mb-3 flex items-center gap-2">
          <ShieldAlert size={16} className="text-[var(--pa-gold)]" /> Recent failed logins
        </h2>
        {failed.length === 0 ? (
          <div className="text-xs text-[var(--pa-text-dim)]">No failed logins recorded.</div>
        ) : (
          <div className="overflow-x-auto bg-[var(--pa-surface)] border border-[var(--pa-border)] rounded-lg">
            <table className="w-full text-sm" data-testid="failed-table">
              <thead className="text-[var(--pa-text-muted)] text-xs uppercase tracking-wider">
                <tr><th className="p-3 text-left">Time</th><th className="p-3 text-left">Email</th><th className="p-3 text-left">IP</th><th className="p-3 text-left">Action</th></tr>
              </thead>
              <tbody>
                {failed.map((r) => (
                  <tr key={r.id} className="border-t border-[var(--pa-border)]">
                    <td className="p-3 text-[var(--pa-text-dim)] text-xs">{new Date(r.ts).toLocaleString()}</td>
                    <td className="p-3 text-[var(--pa-text)]">{r.user_email}</td>
                    <td className="p-3 text-[var(--pa-text-muted)] font-mono text-xs">{r.ip}</td>
                    <td className="p-3 text-[var(--pa-text-muted)] text-xs">{r.action}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* PHI access */}
      <section className="mb-12">
        <h2 className="font-serif text-xl text-[var(--pa-text)] mb-3 flex items-center gap-2">
          <FileLock size={16} className="text-[var(--pa-gold)]" /> Recent PHI access
        </h2>
        {phi.length === 0 ? (
          <div className="text-xs text-[var(--pa-text-dim)]">No PHI access events recorded.</div>
        ) : (
          <div className="overflow-x-auto bg-[var(--pa-surface)] border border-[var(--pa-border)] rounded-lg">
            <table className="w-full text-sm" data-testid="phi-table">
              <thead className="text-[var(--pa-text-muted)] text-xs uppercase tracking-wider">
                <tr>
                  <th className="p-3 text-left">Time</th>
                  <th className="p-3 text-left">User</th>
                  <th className="p-3 text-left">Action</th>
                  <th className="p-3 text-left">Resource</th>
                  <th className="p-3 text-left">IP</th>
                </tr>
              </thead>
              <tbody>
                {phi.map((r) => (
                  <tr key={r.id} className="border-t border-[var(--pa-border)]">
                    <td className="p-3 text-[var(--pa-text-dim)] text-xs">{new Date(r.ts).toLocaleString()}</td>
                    <td className="p-3 text-[var(--pa-text)]">{r.user_email}</td>
                    <td className="p-3 text-[var(--pa-text-muted)] text-xs font-mono">{r.action}</td>
                    <td className="p-3 text-[var(--pa-text-muted)] text-xs">{r.resource_type}{r.resource_id ? ` · ${r.resource_id.slice(0, 8)}…` : ""}</td>
                    <td className="p-3 text-[var(--pa-text-muted)] font-mono text-xs">{r.ip}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </Layout>
  );
}
